import React, { useLayoutEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { useTranslation } from 'react-i18next'

// Styles
import { applyTextStyle } from '../styles/typography'

// Types
import { ITheme } from '../context/AppContext'

type Surface = 'lightest' | 'primaryLight' | 'textLightest'

// A todo segment sits one step against the surface the bar is placed on
const TODO_COLOR: Record<Surface, keyof ITheme['colors']> = {
  lightest: 'textLightest',
  primaryLight: 'textLighter',
  textLightest: 'textLighter',
}

interface IProps {
  // Step names shown under the segments, or just a count for a bar without labels
  steps: string[] | number
  // Index of the step the user is on
  current: number
  // The background the bar sits on
  surface?: Surface
  className?: string
}

// One segment per step across the full width. Under a fixed title it carries the
// step names; above a title that changes per step it goes without (see
// knowledge/design). Labels that do not all fit leave only the current one.
const StepBar: React.FC<IProps> = ({
  steps,
  current,
  surface = 'lightest',
  className,
}) => {
  const { t } = useTranslation()
  const listRef = useRef<HTMLOListElement>(null)
  const [compact, setCompact] = useState(false)
  const labels = typeof steps === 'number' ? undefined : steps
  const count = labels ? labels.length : (steps as number)
  const labelText = labels?.join('|')

  // A label is cut off when its text is wider than its own segment
  useLayoutEffect(() => {
    const list = listRef.current
    if (!list || !labelText || typeof ResizeObserver === 'undefined') return
    const measure = () =>
      setCompact(
        Array.from(list.querySelectorAll('span')).some(
          (label) => label.scrollWidth > (label.parentElement?.clientWidth ?? 0)
        )
      )
    const observer = new ResizeObserver(measure)
    observer.observe(list)
    return () => observer.disconnect()
  }, [labelText])

  return (
    <Steps
      ref={listRef}
      className={className}
      aria-label={t('general.stepOf', { current: current + 1, total: count })}
    >
      {Array.from({ length: count }).map((_, index) => (
        <Step
          key={index}
          $color={index <= current ? 'primary' : TODO_COLOR[surface]}
          aria-current={index === current ? 'step' : undefined}
        >
          {labels && (
            <Label
              $current={index === current}
              $hidden={compact && index !== current}
              $overflow={compact && index === current}
              $alignEnd={index >= count / 2}
            >
              {labels[index]}
            </Label>
          )}
        </Step>
      ))}
    </Steps>
  )
}

export default StepBar

const Steps = styled.ol`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  gap: 4px;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
`

const Step = styled.li<{ $color: keyof ITheme['colors'] }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;

  &::before {
    content: '';
    height: 4px;
    border-radius: 999px;
    background-color: ${({ theme, $color }) => theme.colors[$color]};
  }
`

const Label = styled.span<{
  $current: boolean
  $hidden: boolean
  $overflow: boolean
  $alignEnd: boolean
}>`
  ${({ $current }) => applyTextStyle($current ? 'captionHeavy' : 'caption')}
  color: ${({ theme, $current }) =>
    $current ? theme.colors.textDark : theme.colors.textLight};
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};

  /* The only label left may run past its segment, towards the middle of the bar */
  ${({ $overflow, $alignEnd }) =>
    $overflow &&
    css`
      overflow: visible;
      width: max-content;
      align-self: ${$alignEnd ? 'flex-end' : 'flex-start'};
    `}
`
